import { useState } from "react";
import { Modal, TextInput, Button, Stack } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { workspaceService } from "../lib/services";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { showErrorNotification, showSuccessNotification } from "../lib/notifications";

interface CreateWorkspaceModalProps {
  opened: boolean;
  onClose: () => void;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function CreateWorkspaceModal({ opened, onClose }: CreateWorkspaceModalProps) {
  const { refetchWorkspaces } = useWorkspace();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function handleNameChange(value: string) {
    setName(value);
    if (!slugTouched) {
      setSlug(toSlug(value));
    }
  }

  function handleClose() {
    setName("");
    setSlug("");
    setSlugTouched(false);
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;

    setSubmitting(true);
    try {
      const created = await workspaceService.create({
        name: name.trim(),
        slug: slug || undefined,
      });
      await refetchWorkspaces();
      handleClose();
      localStorage.setItem("showrunner:lastWorkspaceId", created.id);
      navigate(`/${created.id}/prompts`);
      showSuccessNotification(`Workspace "${created.name}" created`);
    } catch (err) {
      showErrorNotification(
        err instanceof Error ? err.message : "Failed to create workspace",
        "Workspace creation failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Create workspace" size="sm">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Workspace name"
            placeholder="My team"
            value={name}
            onChange={(e) => handleNameChange(e.currentTarget.value)}
            required
            data-autofocus
          />
          <TextInput
            label="Slug"
            description="Used in URLs and API access"
            placeholder="my-team"
            value={slug}
            onChange={(e) => {
              setSlug(e.currentTarget.value);
              setSlugTouched(true);
            }}
          />
          <Button type="submit" loading={submitting} disabled={!name.trim()}>
            Create workspace
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
